import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect, Provider } from 'react-redux';
import { compose } from 'redux';
import { hot } from 'react-hot-loader';
import axios from 'axios';
import ENDPOINTS from 'constants/endpoints';
import { logger } from 'packages';
import {
    withTranslator,
    useTranslator,
    useCurrentLanguage,
    withUrlLocalizer
} from 'static/script/app/lang';
import globalStore from 'tradera-state/configure-store';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import { getItemCardText } from 'components/item-card/helpers';
import { lazyLoadSettings } from 'components/item-card/config';
import initData from 'init-data';
import { ItemCard } from '@tradera/blueprint/components/lib/item-card';
import { ApplicationErrorBoundary } from 'components';
import LazyLoad from 'static/script/app/boot/lazy-load';
import { registerLiveUpdates } from 'utils/registerLiveUpdates';

const ITEM_CARD_PLACEHOLDER_COUNT = 12;

const LastChance = props => (
    <ApplicationErrorBoundary>
        <Provider store={globalStore}>
            <LastChanceNewConnected {...props} />
        </Provider>
    </ApplicationErrorBoundary>
);

const LastChanceNew = props => {
    const {
        items,
        isLoggedIn,
        preferredCurrency,
        addItems,
        openQuickView,
        toggleWishlist,
        toLocalizedUrl,
        updateItem
    } = props;

    const [state, setState] = useState({
        loading: true,
        error: false,
        itemIds: []
    });

    const { language } = useCurrentLanguage();

    useEffect(() => {
        axios
            .get(ENDPOINTS.LAST_CHANCE)
            .then(({ data }) => {
                let itemIds = [];
                if (data) {
                    addItems(data);
                    itemIds = data.map(item => item.itemId);
                }
                return setState({
                    loading: false,
                    error: false,
                    itemIds
                });
            })
            .catch(e => {
                logger(e);
                setState({
                    loading: false,
                    error: true,
                    itemIds: []
                });
            });
    }, [addItems]);

    useEffect(() => {
        // Hack to ensure the painting of dom has happened before we recall lazyload
        window.requestAnimationFrame(() => {
            LazyLoad();
        });
    });

    useEffect(() => {
        if (initData.pageSpecificData.BuyingEventPusherEnabled) {
            return registerLiveUpdates({
                url: initData.pageSpecificData.BuyingEventPusherServerUrl,
                onConnect: socket => {
                    for (let itemId of state.itemIds) {
                        socket.emit('joinChannel', itemId);
                    }
                },
                onClientEvent: data => {
                    switch (data.Type) {
                        case 'ItemSold':
                        case 'ItemUnSold':
                            break;
                        case 'AuctionLeadingBidUpdated':
                        case 'IAuctionLeadingBidUpdated':
                            updateItem({
                                itemId: data.Event.AuctionId,
                                item: {
                                    currentBid: data.Event.BidAmount,
                                    totalBids: data.Event.NumberOfBids
                                }
                            });
                            break;
                    }
                }
            });
        }
    }, [state.itemIds, updateItem]);

    const { loading, error, itemIds } = state;
    const { t } = useTranslator();
    const itemCardText = useMemo(() => getItemCardText(t), [t]);

    const handleQuickView = useCallback(
        itemId => {
            openQuickView(itemId, itemIds);
        },
        [itemIds, openQuickView]
    );

    if (error || (!loading && items.length === 0)) return null;

    let displayItems = [];
    if (loading) {
        for (let index = 0; index < ITEM_CARD_PLACEHOLDER_COUNT; index++) {
            displayItems.push({
                itemId: index,
                shortDescription: ''
            });
        }
    } else {
        displayItems = itemIds.map(itemId => items[itemId]);
    }

    return (
        <section className="bp container">
            <div className="row py-3 mt-4">
                <div className="w-100 text-center">
                    <h3 className="text-styled">Sista Chansen</h3>
                </div>
            </div>
            <div className="item-card-layout layout-grid row">
                {displayItems.map(item => (
                    <div
                        key={item.itemId}
                        className="item-card-container px-3 pb-2">
                        <ItemCard
                            animatePriceLabel={true}
                            animateTimeLeft={
                                initData.featureSwitches[
                                    'item-card-time-left-animation'
                                ]
                            }
                            isLoggedIn={isLoggedIn}
                            item={item}
                            lazy={lazyLoadSettings}
                            onAddToWishlist={toggleWishlist}
                            onQuickView={handleQuickView}
                            onRemoveFromWishlist={toggleWishlist}
                            preferredCurrency={preferredCurrency}
                            ready={!!item.itemUrl}
                            language={language}
                            text={itemCardText}
                        />
                    </div>
                ))}
            </div>
            <div className="row">
                <div className="col-12 text-center">
                    <a
                        href={toLocalizedUrl('/campaign/sista-chansen')}
                        role="button"
                        className="btn btn-dark btn-wide"
                        data-ga-category="StartPage"
                        data-ga-action="Navigation"
                        data-ga-label="Last chance button below">
                        {`Visa fler ->`}
                    </a>
                </div>
            </div>
        </section>
    );
};

const mapStateToProps = (state, ownProps) => ({
    items: state.items.cache,
    isLoggedIn: state.member.isLoggedIn,
    preferredCurrency: state.multiCurrency.preferredCurrency,
    ...ownProps
});

const mapDispatchToProps = dispatch => ({
    addItems: items => dispatch(itemActions.add(items)),
    openQuickView: (itemId, itemIds) =>
        dispatch(
            uiActions.openQuickView({
                itemIds,
                itemId,
                openedFrom: 'Last Chance'
            })
        ),
    toggleWishlist: itemId => dispatch(itemActions.wishlist(itemId)),
    updateItem: payload => dispatch(itemActions.updateItem(payload))
});

const LastChanceNewConnected = hot(module)(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(LastChanceNew)
);

export default compose(
    withUrlLocalizer,
    withTranslator,
    hot(module)
)(LastChance);
